import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Navigate, useSearchParams, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faUser, faEnvelope, faPhone, faMapMarkerAlt, faLock, faSpinner, faCheck, faArrowLeft, faShieldAlt, faKey, faCalendar, faPercent, faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { AuroraBackground } from '../ui/aurora-background';
import { subscriptionAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SEO from '../SEO';
import { seoConfig } from '../../utils/seoData';
import { encryptPaymentInfoHybrid } from '../../utils/encryption';
import { Package } from '../../types/models';
import { sortedCountryCodes, CountryCode } from '../../utils/countryCodes';

const YEARLY_DISCOUNT = 0.1; // 10% discount for yearly payment

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('checkout');
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t: tCommon } = useTranslation('common');
  const { business, refreshBusinessProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [packages, setPackages] = useState<Package[]>([]);
  const [monthlyPackage, setMonthlyPackage] = useState<Package | null>(null);
  const [yearlyPackage, setYearlyPackage] = useState<Package | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(sortedCountryCodes.find(c => c.code === 'TR') || sortedCountryCodes[0]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const countryRef = useRef<HTMLDivElement | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    surname?: string;
    email?: string;
    gsmNumber?: string;
    identityNumber?: string;
    address?: string;
    city?: string;
    zipCode?: string;
    country?: string;
  }>({});
  
  // User information
  const [formData, setFormData] = useState({
    // Personal info
    name: '',
    surname: '',
    email: business?.email || '',
    gsmNumber: business?.phone || '',
    identityNumber: '',
    
    // Address
    address: '',
    city: 'Istanbul',
    country: 'Turkey',
    zipCode: '34000',
    
    // Card info
    cardHolderName: '',
    cardNumber: '',
    expireMonth: '',
    expireYear: '',
    cvc: '',
    registerConsumerCard: false,
  });

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!countrySearchQuery.trim()) {
      return sortedCountryCodes;
    }
    const query = countrySearchQuery.toLowerCase();
    return sortedCountryCodes.filter(country => 
      country.name.toLowerCase().includes(query) ||
      country.nameEn.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query) ||
      country.dialCode.includes(query)
    );
  }, [countrySearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    // Get public key for encryption
    const fetchPublicKey = async () => {
      try {
        const response = await subscriptionAPI.getPublicKey();
        // API returns text/plain, so response.data is the public key string
        const key = typeof response.data === 'string' ? response.data : response.data?.toString();
        if (key) {
          setPublicKey(key);
        }
      } catch (err) {
        console.error('Could not fetch public key:', err);
        setError(t('publicKeyError'));
      }
    };

    // Get packages
    const fetchPackages = async () => {
      try {
        setPackagesLoading(true);
        const response = await subscriptionAPI.getPackages();
        const packagesData = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const activePackages = packagesData.filter((pkg: Package) => pkg.isActive);
        setPackages(activePackages);

        // Separate monthly and yearly packages
        const monthly = activePackages.find((p: Package) => p.paymentInterval === 'MONTHLY');
        const yearly = activePackages.find((p: Package) => p.paymentInterval === 'YEARLY');
        
        setMonthlyPackage(monthly || null);
        setYearlyPackage(yearly || null);

        // Try to get package from URL parameter or default to monthly
        const packageId = searchParams.get('packageId');
        if (packageId) {
          const pkg = activePackages.find((p: Package) => p._id === packageId);
          if (pkg) {
            setSelectedPackage(pkg);
          } else if (monthly) {
            setSelectedPackage(monthly);
          } else if (activePackages.length > 0) {
            setSelectedPackage(activePackages[0]);
          }
        } else if (monthly) {
          setSelectedPackage(monthly);
        } else if (activePackages.length > 0) {
          setSelectedPackage(activePackages[0]);
        }
      } catch (err: any) {
        console.error('Error fetching packages:', err);
        setError(t('error'));
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPublicKey();
    fetchPackages();
  }, [searchParams]);

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name || name.trim().length === 0) {
      return 'Ad gereklidir';
    }
    if (name.trim().length > 100) {
      return 'Ad 100 karakterden uzun olamaz';
    }
    return undefined;
  };

  const validateSurname = (surname: string): string | undefined => {
    if (!surname || surname.trim().length === 0) {
      return 'Soyad gereklidir';
    }
    if (surname.trim().length > 100) {
      return 'Soyad 100 karakterden uzun olamaz';
    }
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email || email.trim().length === 0) {
      return 'E-posta adresi gereklidir';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Geçerli bir e-posta adresi giriniz';
    }
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (phone && phone.trim().length > 0) {
      // Remove spaces and special characters for validation
      const cleanedPhone = phone.replace(/\s+/g, '').replace(/[-\/\(\)]/g, '');
      // Check if phone contains only digits
      if (!/^\d+$/.test(cleanedPhone)) {
        return 'Telefon numarası sadece rakamlardan oluşmalıdır';
      }
      // Check minimum length (at least 7 digits, max 15 digits for international format)
      if (cleanedPhone.length < 7) {
        return 'Telefon numarası en az 7 haneli olmalıdır';
      }
      if (cleanedPhone.length > 15) {
        return 'Telefon numarası en fazla 15 haneli olabilir';
      }
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Clear field error when user starts typing
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Real-time validation
    let error: string | undefined;
    if (name === 'name') {
      error = validateName(value);
    } else if (name === 'surname') {
      error = validateSurname(value);
    } else if (name === 'email') {
      error = validateEmail(value);
    } else if (name === 'gsmNumber') {
      error = validatePhone(value);
    }

    if (error) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
  };

  const formatCardNumber = (value: string) => {
    // Remove non-digits
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      cardNumber: formatted
    }));
  };

  // Calculate prices based on selected package
  const currentPrice = selectedPackage?.price || 0;
  const currentPriceDisplay = selectedPackage 
    ? `$${selectedPackage.price}` 
    : '$0';
  
  // Calculate monthly equivalent for yearly package (always show for yearly package card)
  const yearlyMonthlyEquivalent = yearlyPackage 
    ? (yearlyPackage.price / 12).toFixed(2)
    : null;
  
  // Calculate savings for yearly package
  const savings = yearlyPackage && monthlyPackage
    ? (monthlyPackage.price * 12 - yearlyPackage.price).toFixed(0)
    : null;
  
  // Monthly equivalent for selected yearly package
  const selectedMonthlyEquivalent = selectedPackage?.paymentInterval === 'YEARLY' && selectedPackage
    ? (selectedPackage.price / 12).toFixed(2)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Validation
    if (!business?._id) {
      setError(t('businessNotFound'));
      return;
    }

    // Validate all fields
    const errors: typeof fieldErrors = {};
    
    const nameError = validateName(formData.name);
    if (nameError) errors.name = nameError;

    const surnameError = validateSurname(formData.surname);
    if (surnameError) errors.surname = surnameError;

    const emailError = validateEmail(formData.email);
    if (emailError) errors.email = emailError;

    if (formData.gsmNumber) {
      const phoneError = validatePhone(formData.gsmNumber);
      if (phoneError) errors.gsmNumber = phoneError;
    } else {
      errors.gsmNumber = 'Telefon numarası gereklidir';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!formData.cardNumber || !formData.expireMonth || !formData.expireYear || !formData.cvc) {
      setError(t('fillCardInfo'));
      return;
    }

    if (!publicKey) {
      setError(t('publicKeyError'));
      return;
    }

    setLoading(true);

    try {
      // Prepare subscription data
      const cardNumber = formData.cardNumber.replace(/\s/g, '');
      
      if (!selectedPackage) {
        setError(t('selectPackage'));
        setLoading(false);
        return;
      }

      const subscriptionData = {
        businessId: business._id,
        packageId: selectedPackage._id,
        productReferenceCode: selectedPackage.productReferenceCode,
        planReferenceCode: selectedPackage.planReferenceCode,
        billingPeriod: selectedPackage.paymentInterval === 'YEARLY' ? 'yearly' : 'monthly',
        price: currentPrice,
        name: formData.name,
        surname: formData.surname,
        gsmNumber: formData.gsmNumber ? `${selectedCountry.dialCode} ${formData.gsmNumber}`.trim() : '',
        identityNumber: formData.identityNumber,
        email: formData.email,
        addressDetails: {
          address: formData.address,
          city: formData.city,
          country: formData.country,
          zipCode: formData.zipCode,
        },
        billingAddress: {
          contactName: `${formData.name} ${formData.surname}`,
          country: formData.country,
          city: formData.city,
          zipCode: formData.zipCode,
          address: formData.address || t('addressNotEntered'),
        },
        shippingAddress: {
          contactName: `${formData.name} ${formData.surname}`,
          country: formData.country,
          city: formData.city,
          zipCode: formData.zipCode,
          address: formData.address || t('addressNotEntered'),
        },
        cardHolderName: formData.cardHolderName || `${formData.name} ${formData.surname}`,
        cardNumber: cardNumber,
        expireMonth: formData.expireMonth,
        expireYear: formData.expireYear,
        cvc: formData.cvc,
        registerConsumerCard: formData.registerConsumerCard,
      };

      // Encrypt payment info using hybrid encryption
      const encrypted = await encryptPaymentInfoHybrid(subscriptionData, publicKey);

      // Send encrypted data to API
      const response = await subscriptionAPI.subscribe(business._id, encrypted);
      
      // Success - refresh profile and redirect to dashboard
      if (response.data?.subscription) {
        try {
          // Refresh business profile to get updated subscription info
          await refreshBusinessProfile();
          // Small delay to ensure context is updated
          setTimeout(() => {
            const currentLang = lang || 'tr';
            navigate(`/${currentLang}/social-media`);
          }, 500);
        } catch (err) {
          console.error('Failed to refresh profile:', err);
          // Still navigate even if refresh fails
          const currentLang = lang || 'tr';
          navigate(`/${currentLang}/social-media`);
        }
      } else {
        setError(t('subscriptionError'));
      }
    } catch (err: any) {
      console.error('Subscription error:', err);
      // Handle server validation errors
      if (err.response?.data?.errors) {
        const serverErrors = err.response.data.errors;
        const fieldErrorMap: typeof fieldErrors = {};
        
        if (serverErrors.name) fieldErrorMap.name = serverErrors.name;
        if (serverErrors.surname) fieldErrorMap.surname = serverErrors.surname;
        if (serverErrors.email) fieldErrorMap.email = serverErrors.email;
        if (serverErrors.gsmNumber) fieldErrorMap.gsmNumber = serverErrors.gsmNumber;
        if (serverErrors.phone) fieldErrorMap.gsmNumber = serverErrors.phone;
        
        if (Object.keys(fieldErrorMap).length > 0) {
          setFieldErrors(fieldErrorMap);
        } else {
          setError(err.response.data.message || err.message || t('subscriptionCreateError'));
        }
      } else {
        setError(err.response?.data?.message || err.message || t('subscriptionCreateError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // If no business, redirect to login
  if (!business) {
    const currentLang = lang || 'tr';
    return <Navigate to={`/${currentLang}/login`} replace />;
  }

  return (
    <>
      <SEO {...seoConfig.login} />
      <AuroraBackground className="w-full h-full overflow-scroll">
        <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden py-10">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>

          {/* Floating Elements */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
          <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

          <div className="relative z-10 w-full max-w-5xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Package Info */}
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
                <button
                  onClick={() => {
                    const currentLang = lang || 'tr';
                    navigate(`/${currentLang}/login`);
                  }}
                  className="flex items-center space-x-2 text-white/80 md:hover:text-white transition-colors mb-6"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  <span>{t('back')}</span>
                </button>

                <h2 className="text-2xl font-bold text-white mb-6">{t('title')}</h2>
                
                {packagesLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <FontAwesomeIcon icon={faSpinner} className="text-purple-400 text-2xl animate-spin" />
                  </div>
                ) : (monthlyPackage || yearlyPackage) ? (
                <div className="mb-6 space-y-4">
                  {/* Monthly Package Card */}
                  {monthlyPackage && (
                    <div
                      onClick={() => setSelectedPackage(monthlyPackage)}
                      className={`cursor-pointer backdrop-blur-xl border-2 rounded-xl p-6 transition-all ${
                        selectedPackage?._id === monthlyPackage._id
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-white/10 bg-white/5 md:hover:border-purple-400/50 md:hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-1">{monthlyPackage.name}</h3>
                          <p className="text-white/70 text-sm">{monthlyPackage.description || t('monthlyPackage')}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-400">
                            ${monthlyPackage.price}
                            <span className="text-lg text-white/70">{t('monthly')}</span>
                          </div>
                        </div>
                      </div>
                      {selectedPackage?._id === monthlyPackage._id && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <ul className="space-y-2">
                            {monthlyPackage.features.map((feature, index) => (
                              <li key={index} className="flex items-start space-x-2 text-white/80 text-sm">
                                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs" />
                                </div>
                                <span className="flex-1">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Yearly Package Card */}
                  {yearlyPackage && (
                    <div
                      onClick={() => setSelectedPackage(yearlyPackage)}
                      className={`cursor-pointer backdrop-blur-xl border-2 rounded-xl p-6 transition-all relative ${
                        selectedPackage?._id === yearlyPackage._id
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-white/10 bg-white/5 md:hover:border-purple-400/50 md:hover:bg-white/10'
                      }`}
                    >
                      {savings && (
                        <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                          ${savings} {t('savings')}
                        </div>
                      )}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-white mb-1">{yearlyPackage.name}</h3>
                          <p className="text-white/70 text-sm">{yearlyPackage.description || t('yearlyPackage')}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-400">
                            ${yearlyPackage.price}
                            <span className="text-lg text-white/70">{t('yearly')}</span>
                          </div>
                          {yearlyMonthlyEquivalent && (
                            <div className="text-sm text-white/60 mt-1">
                              (${yearlyMonthlyEquivalent}{t('monthly')})
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedPackage?._id === yearlyPackage._id && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <ul className="space-y-2">
                            {yearlyPackage.features.map((feature, index) => (
                              <li key={index} className="flex items-start space-x-2 text-white/80 text-sm">
                                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs" />
                                </div>
                                <span className="flex-1">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Package Details */}
                  {selectedPackage && (
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <div className="text-center mb-4">
                        <p className="text-white/70 text-sm mb-2">{t('selectedPackage')}</p>
                        <div className="text-2xl font-bold text-white">
                          {currentPriceDisplay}
                          <span className="text-lg text-white/70">
                            {selectedPackage.paymentInterval === 'YEARLY' ? t('yearly') : t('monthly')}
                          </span>
                        </div>
                        {selectedMonthlyEquivalent && (
                          <p className="text-white/60 text-sm mt-1">
                            {t('monthlyEquivalent')} ${selectedMonthlyEquivalent}{t('monthly')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-white/70">{t('noPackages')}</p>
                  </div>
                )}

                {/* Security Badge in Package Info */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mt-6">
                  <div className="flex items-center space-x-3">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-green-400 text-2xl" />
                    <div>
                      <h4 className="text-green-400 font-semibold text-sm">{t('securePayment')}</h4>
                      <p className="text-white/60 text-xs mt-1">{t('securePaymentDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Checkout Form */}
              <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 shadow-2xl">
                <h2 className="text-2xl font-bold text-white mb-6">{t('paymentInfo')}</h2>

                {error && (
                  <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-center mb-4">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <FontAwesomeIcon icon={faUser} />
                      <span>{t('personalInfo')}</span>
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('name')} {t('required')}</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                            fieldErrors.name ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                          }`}
                          required
                        />
                        {fieldErrors.name && (
                          <p className="mt-1 text-xs text-red-400">{fieldErrors.name}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('surname')} {t('required')}</label>
                        <input
                          type="text"
                          name="surname"
                          value={formData.surname}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                            fieldErrors.surname ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                          }`}
                          required
                        />
                        {fieldErrors.surname && (
                          <p className="mt-1 text-xs text-red-400">{fieldErrors.surname}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2 flex items-center space-x-2">
                        <FontAwesomeIcon icon={faEnvelope} className="text-sm" />
                        <span>{t('email')} {t('required')}</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                          fieldErrors.email ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                        }`}
                        required
                      />
                      {fieldErrors.email && (
                        <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2 flex items-center space-x-2">
                        <FontAwesomeIcon icon={faPhone} className="text-sm" />
                        <span>{t('phone')} {t('required')}</span>
                      </label>
                      <div className="flex gap-2">
                        {/* Country Code Selector */}
                        <div className="relative" ref={countryRef}>
                          <button
                            type="button"
                            onClick={() => {
                              setCountryDropdownOpen(!countryDropdownOpen);
                              setCountrySearchQuery('');
                            }}
                            className="flex items-center gap-2 px-3 h-[50px] bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 min-w-[140px]"
                          >
                            <span className="text-lg">{selectedCountry.flag}</span>
                            <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
                            <FontAwesomeIcon icon={faChevronDown} className={`text-white/50 text-xs transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {countryDropdownOpen && (
                            <div className="absolute z-30 mt-2 w-80 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-xl overflow-hidden">
                              {/* Search Input */}
                              <div className="p-3 border-b border-white/10">
                                <div className="relative">
                                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                                  <input
                                    type="text"
                                    value={countrySearchQuery}
                                    onChange={(e) => setCountrySearchQuery(e.target.value)}
                                    placeholder={tCommon('countryCodeSearchPlaceholder')}
                                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              {/* Country List */}
                              <div className="max-h-64 overflow-y-auto">
                                {filteredCountries.length > 0 ? (
                                  filteredCountries.map((country) => (
                                    <button
                                      key={country.code}
                                      type="button"
                                      onClick={() => {
                                        setSelectedCountry(country);
                                        setCountryDropdownOpen(false);
                                        setCountrySearchQuery('');
                                      }}
                                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/10 transition-colors ${
                                        selectedCountry.code === country.code ? 'bg-white/10' : ''
                                      }`}
                                    >
                                      <span className="text-xl">{country.flag}</span>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-white text-sm font-medium truncate">{country.name}</div>
                                        <div className="text-white/60 text-xs truncate">{country.nameEn}</div>
                                      </div>
                                      <div className="flex items-center gap-2 text-white/70 text-sm">
                                        <span className="font-medium">{country.dialCode}</span>
                                        <span className="text-xs">({country.code})</span>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-8 text-center text-white/60 text-sm">
                                    {tCommon('countryNotFound')}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Phone Number Input */}
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faPhone} className="text-white/40 text-sm" />
                          </div>
                          <input
                            type="tel"
                            name="gsmNumber"
                            value={formData.gsmNumber}
                            onChange={handleInputChange}
                            placeholder="555 123 4567"
                            className={`w-full pl-10 pr-4 h-[50px] bg-white/5 border rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                              fieldErrors.gsmNumber ? 'border-red-500/50 focus:ring-red-500' : 'border-white/10'
                            }`}
                            required
                          />
                        </div>
                      </div>
                      {fieldErrors.gsmNumber ? (
                        <p className="mt-1 text-xs text-red-400">{fieldErrors.gsmNumber}</p>
                      ) : (
                        <p className="mt-1 text-xs text-white/50">
                          {tCommon('fullPhoneNumber')}: {selectedCountry.dialCode} {formData.gsmNumber || '...'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('identityNumber')}</label>
                      <input
                        type="text"
                        name="identityNumber"
                        value={formData.identityNumber}
                        onChange={handleInputChange}
                        maxLength={11}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      <span>{t('addressInfo')}</span>
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('address')}</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('city')}</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('zipCode')}</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                      <FontAwesomeIcon icon={faCreditCard} />
                      <span>{t('cardInfo')}</span>
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('cardHolderName')} {t('required')}</label>
                      <input
                        type="text"
                        name="cardHolderName"
                        value={formData.cardHolderName}
                        onChange={handleInputChange}
                        placeholder={`${formData.name} ${formData.surname}`}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">{t('cardNumber')} {t('required')}</label>
                      <input
                        type="text"
                        name="cardNumber"
                        value={formData.cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('expireMonth')} {t('required')}</label>
                        <select
                          name="expireMonth"
                          value={formData.expireMonth}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                          required
                        >
                          <option value="">{t('month')}</option>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                            <option key={month} value={String(month).padStart(2, '0')}>
                              {String(month).padStart(2, '0')}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('expireYear')} {t('required')}</label>
                        <select
                          name="expireYear"
                          value={formData.expireYear}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                          required
                        >
                          <option value="">{t('year')}</option>
                          {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(year => (
                            <option key={year} value={String(year)}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">{t('cvc')} {t('required')}</label>
                        <input
                          type="text"
                          name="cvc"
                          value={formData.cvc}
                          onChange={handleInputChange}
                          placeholder="123"
                          maxLength={3}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="registerConsumerCard"
                        id="registerConsumerCard"
                        checked={formData.registerConsumerCard}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-purple-600 bg-white/5 border-white/20 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="registerConsumerCard" className="text-sm text-white/70">
                        {t('saveCard')}
                      </label>
                    </div>
                  </div>

                  {/* Security Information */}
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-green-400 text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-green-400 font-semibold mb-2 flex items-center space-x-2">
                          <span>{t('encryptionInfo.title')}</span>
                          <FontAwesomeIcon icon={faLock} className="text-sm" />
                        </h4>
                        <div className="space-y-2 text-sm text-white/80">
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.endToEnd')}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.rsa')}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.hybrid')}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.pci')}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.ssl')}</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <FontAwesomeIcon icon={faCheck} className="text-green-400 text-xs mt-1 flex-shrink-0" />
                            <span>{t('encryptionInfo.dataSecurity')}</span>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-green-500/20">
                          <p className="text-xs text-white/60">
                            <FontAwesomeIcon icon={faKey} className="text-green-400 mr-1" />
                            {t('encryptionInfo.browserNote')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !publicKey}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-xl text-white font-semibold md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>{t('processing')}</span>
                      </>
                    ) : !publicKey ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        <span>{t('preparingSecurity')}</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faLock} />
                        <span>{currentPriceDisplay} {t('paySecurely')}</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </AuroraBackground>
    </>
  );
};

export default CheckoutPage;

